/**
 * Routes definition. Per the plan, file-based-style organisation under
 * src/routes/. R0 only ships login + cockpit landing. R1+ adds Inbox,
 * Live, Customers, etc.
 */

import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { useUiStore, applyUiPrefs } from '@/store/ui'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { RoleGate } from '@/components/RoleGate'

const LoginPage = lazy(() => import('@/routes/login'))
const AcceptInvitePage = lazy(() => import('@/routes/accept-invite'))
const CockpitPage = lazy(() => import('@/routes/index'))
const CustomersListPage = lazy(() => import('@/routes/customers/index'))
const CustomerDetailPage = lazy(() => import('@/routes/customers/[id]'))
const SubscriptionsPage = lazy(() => import('@/routes/subscriptions/index'))
const RelayPage = lazy(() => import('@/routes/services/relay'))
const RelayTransactionsPage = lazy(() => import('@/routes/services/relay-transactions'))
const RelayAnalyticsPage = lazy(() => import('@/routes/services/relay-analytics'))
const RelayBundlesPage = lazy(() => import('@/routes/services/relay-bundles'))
const RelayNodesPage = lazy(() => import('@/routes/services/relay-nodes'))
const RelayLatencyPage = lazy(() => import('@/routes/services/relay-latency'))
const RelayWrapPage = lazy(() => import('@/routes/services/relay-wrap'))
const GrpcPage = lazy(() => import('@/routes/services/grpc'))
const RpcPage = lazy(() => import('@/routes/services/rpc'))
const RpcEndpointsPage = lazy(() => import('@/routes/services/rpc-endpoints'))
const RpcDiscoveryPage = lazy(() => import('@/routes/services/rpc-discovery'))
const ShredStreamPage = lazy(() => import('@/routes/services/shredstream'))
const JitoShredsPage = lazy(() => import('@/routes/services/jito-shreds'))
const SlipstreamPage = lazy(() => import('@/routes/services/slipstream'))
const BotOverviewPage = lazy(() => import('@/routes/bots/overview'))
const BotPricesPage = lazy(() => import('@/routes/bots/prices'))
const BotCataloguePage = lazy(() => import('@/routes/bots/catalogue'))
const BotDeploymentsPage = lazy(() => import('@/routes/bots/deployments'))
const BotDeploymentPage = lazy(() => import('@/routes/bots/deployment'))
const BotInfrastructurePage = lazy(() => import('@/routes/bots/infrastructure'))
const GetShredsPage = lazy(() => import('@/routes/services/getshreds'))
const DedicatedShredsPage = lazy(() => import('@/routes/services/dedicated-shreds'))
const VpsPage = lazy(() => import('@/routes/services/vps'))
const VpsFleetPage = lazy(() => import('@/routes/services/vps-fleet'))
const VpsFleetHostPage = lazy(() => import('@/routes/services/vps-fleet-host'))
const E2ETestsPage = lazy(() => import('@/routes/services/e2e-tests'))
const SolTracePage = lazy(() => import('@/routes/services/sol-trace'))
const SolTraceAnalyticsPage = lazy(() => import('@/routes/services/sol-trace-analytics'))
const SolTraceCustomerPage = lazy(() => import('@/routes/services/sol-trace-customer'))
const InboxPage = lazy(() => import('@/routes/inbox/index'))
const LiveVisitorsPage = lazy(() => import('@/routes/live/index'))
const AdminUsersPage = lazy(() => import('@/routes/admin/users'))
const AdminAuditPage = lazy(() => import('@/routes/admin/audit'))
const ServiceKeysPage = lazy(() => import('@/routes/admin/service-keys'))
const OrphansPage = lazy(() => import('@/routes/admin/orphans'))
const ProxyHubPage = lazy(() => import('@/routes/admin/proxy-hub'))
const InvoicesPage = lazy(() => import('@/routes/finance/invoices'))
const PaymentsPage = lazy(() => import('@/routes/finance/payments'))
const WalletsPage = lazy(() => import('@/routes/finance/wallets'))
const ReportsPage = lazy(() => import('@/routes/finance/reports'))
const JournalPage = lazy(() => import('@/routes/finance/journal'))
const AccountsPage = lazy(() => import('@/routes/finance/accounts'))
const ExpensesPage = lazy(() => import('@/routes/finance/expenses'))
const StatementsPage = lazy(() => import('@/routes/finance/statements'))
const ReconciliationPage = lazy(() => import('@/routes/finance/reconciliation'))
const EmployeesPage2 = lazy(() => import('@/routes/finance/employees'))
const PayrollPage = lazy(() => import('@/routes/finance/payroll'))
const OpsServicesPage = lazy(() => import('@/routes/ops/services'))
const OpsLogsPage = lazy(() => import('@/routes/ops/logs'))
const OpsSystemPage = lazy(() => import('@/routes/ops/system'))
const PipelinePage = lazy(() => import('@/routes/crm/pipeline'))
const DealsPage = lazy(() => import('@/routes/crm/deals'))
const TemplatesPage = lazy(() => import('@/routes/crm/templates'))
const SegmentsPage = lazy(() => import('@/routes/crm/segments'))
const SequencesPage = lazy(() => import('@/routes/crm/sequences'))
const TasksPage = lazy(() => import('@/routes/crm/tasks'))
const AutomationsPage = lazy(() => import('@/routes/crm/automations'))
const AnalyticsRealtimePage = lazy(() => import('@/routes/analytics/realtime'))
const AnalyticsPagesPage = lazy(() => import('@/routes/analytics/pages'))
const AnalyticsSourcesPage = lazy(() => import('@/routes/analytics/sources'))
const AnalyticsFunnelsPage = lazy(() => import('@/routes/analytics/funnels'))
const AnalyticsAttributionPage = lazy(() => import('@/routes/analytics/attribution'))
const AnalyticsSearchPage = lazy(() => import('@/routes/analytics/search'))
const AnalyticsHeatmapsPage = lazy(() => import('@/routes/analytics/heatmaps'))
const AnalyticsReplaysPage = lazy(() => import('@/routes/analytics/replays'))
const AnalyticsVisitorsPage = lazy(() => import('@/routes/analytics/visitors'))
const MarketingCampaignsPage = lazy(() => import('@/routes/marketing/campaigns/index'))
const MarketingCampaignNewPage = lazy(() => import('@/routes/marketing/campaigns/new'))
const MarketingCampaignDetailPage = lazy(() => import('@/routes/marketing/campaigns/[id]'))
const MarketingSegmentsPage = lazy(() => import('@/routes/marketing/segments'))
const MarketingTemplatesPage = lazy(() => import('@/routes/marketing/templates'))
const WorkspacePage = lazy(() => import('@/routes/workspace/index'))
const WorkspaceCustomerPage = lazy(() => import('@/routes/workspace/customer'))
const DiscountsPage = lazy(() => import('@/routes/workspace/discounts'))
const ReferralsPage = lazy(() => import('@/routes/workspace/referrals'))
const PricingPage = lazy(() => import('@/routes/workspace/pricing'))

function PageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center text-ink-400 text-sm">
      Loading…
    </div>
  )
}

function NotFound() {
  return (
    <div className="px-8 py-16 text-center">
      <h1 className="text-2xl font-semibold text-ink-900 mb-2">Not found</h1>
      <p className="text-sm text-ink-500">
        That page doesn&apos;t exist yet. Try <span className="num">⌘K</span> to
        navigate.
      </p>
    </div>
  )
}

export default function App() {
  const theme = useUiStore((s) => s.theme)
  const density = useUiStore((s) => s.density)

  useEffect(() => {
    applyUiPrefs()
  }, [theme, density])

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          <Route
            element={
              <RoleGate>
                <DashboardLayout />
              </RoleGate>
            }
          >
            <Route index element={<CockpitPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route
              path="live"
              element={
                <RoleGate requires="crm.read">
                  <LiveVisitorsPage />
                </RoleGate>
              }
            />
            <Route
              path="customers"
              element={
                <RoleGate requires="customers.read">
                  <CustomersListPage />
                </RoleGate>
              }
            />
            <Route
              path="customers/:id"
              element={
                <RoleGate requires="customers.read">
                  <CustomerDetailPage />
                </RoleGate>
              }
            />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="services/relay" element={<RelayPage />} />
            <Route path="services/relay/transactions" element={<RelayTransactionsPage />} />
            <Route path="services/relay/analytics" element={<RelayAnalyticsPage />} />
            <Route path="services/relay/bundles" element={<RelayBundlesPage />} />
            <Route path="services/relay/nodes" element={<RelayNodesPage />} />
            <Route path="services/relay/latency" element={<RelayLatencyPage />} />
            <Route path="services/relay/wrap" element={<RelayWrapPage />} />
            <Route path="services/grpc" element={<GrpcPage />} />
            <Route path="services/rpc" element={<RpcPage />} />
            <Route
              path="services/rpc-endpoints"
              element={
                <RoleGate requires="services.read">
                  <RpcEndpointsPage />
                </RoleGate>
              }
            />
            <Route path="services/rpc-discovery" element={<RpcDiscoveryPage />} />
            <Route path="services/shredstream" element={<ShredStreamPage />} />
            <Route path="services/jito-shreds" element={<JitoShredsPage />} />
            <Route path="services/slipstream" element={<SlipstreamPage />} />
            <Route
              path="bots"
              element={
                <RoleGate requires="bots.read">
                  <BotOverviewPage />
                </RoleGate>
              }
            />
            <Route
              path="bots/prices"
              element={
                <RoleGate requires="bots.read">
                  <BotPricesPage />
                </RoleGate>
              }
            />
            <Route
              path="bots/catalogue"
              element={
                <RoleGate requires="bots.read">
                  <BotCataloguePage />
                </RoleGate>
              }
            />
            <Route
              path="bots/deployments"
              element={
                <RoleGate requires="bots.read">
                  <BotDeploymentsPage />
                </RoleGate>
              }
            />
            <Route
              path="bots/deployments/:id"
              element={
                <RoleGate requires="bots.read">
                  <BotDeploymentPage />
                </RoleGate>
              }
            />
            <Route
              path="bots/infrastructure"
              element={
                <RoleGate requires="bots.read">
                  <BotInfrastructurePage />
                </RoleGate>
              }
            />
            <Route path="services/getshreds" element={<GetShredsPage />} />
            <Route path="services/dedicated-shreds" element={<DedicatedShredsPage />} />
            <Route path="services/vps" element={<VpsPage />} />
            <Route path="services/vps/fleet" element={<VpsFleetPage />} />
            <Route path="services/vps/fleet/:server_id" element={<VpsFleetHostPage />} />
            <Route path="services/e2e-tests" element={<E2ETestsPage />} />
            <Route path="services/sol-trace" element={<SolTracePage />} />
            <Route path="services/sol-trace/analytics" element={<SolTraceAnalyticsPage />} />
            <Route path="services/sol-trace/customers/:id" element={<SolTraceCustomerPage />} />
            <Route
              path="finance/invoices"
              element={
                <RoleGate requires="invoices.read">
                  <InvoicesPage />
                </RoleGate>
              }
            />
            <Route
              path="finance/payments"
              element={
                <RoleGate requires="payments.read">
                  <PaymentsPage />
                </RoleGate>
              }
            />
            <Route path="finance/wallets" element={<WalletsPage />} />
            <Route path="finance/reports" element={<ReportsPage />} />
            <Route
              path="finance/journal"
              element={
                <RoleGate requires="accounting.read">
                  <JournalPage />
                </RoleGate>
              }
            />
            <Route
              path="finance/accounts"
              element={
                <RoleGate requires="accounting.read">
                  <AccountsPage />
                </RoleGate>
              }
            />
            <Route
              path="finance/expenses"
              element={
                <RoleGate requires="accounting.read">
                  <ExpensesPage />
                </RoleGate>
              }
            />
            <Route
              path="finance/statements"
              element={
                <RoleGate requires="accounting.read">
                  <StatementsPage />
                </RoleGate>
              }
            />
            <Route
              path="finance/reconciliation"
              element={
                <RoleGate requires="accounting.read">
                  <ReconciliationPage />
                </RoleGate>
              }
            />
            <Route
              path="finance/employees"
              element={
                <RoleGate requires="payroll.read">
                  <EmployeesPage2 />
                </RoleGate>
              }
            />
            <Route
              path="finance/payroll"
              element={
                <RoleGate requires="payroll.read">
                  <PayrollPage />
                </RoleGate>
              }
            />
            <Route path="finance/*" element={<Placeholder name="Finance" release="R2" />} />
            <Route path="ops/services" element={<OpsServicesPage />} />
            <Route
              path="ops/logs"
              element={
                <RoleGate requires="logs.read">
                  <OpsLogsPage />
                </RoleGate>
              }
            />
            <Route path="ops/system" element={<OpsSystemPage />} />
            <Route path="crm/pipeline" element={<PipelinePage />} />
            <Route path="crm/deals" element={<DealsPage />} />
            <Route path="crm/templates" element={<TemplatesPage />} />
            <Route path="crm/segments" element={<SegmentsPage />} />
            <Route path="crm/sequences" element={<SequencesPage />} />
            <Route path="crm/tasks" element={<TasksPage />} />
            <Route path="crm/automations" element={<AutomationsPage />} />
            <Route
              path="marketing/campaigns"
              element={
                <RoleGate requires="crm.write">
                  <MarketingCampaignsPage />
                </RoleGate>
              }
            />
            <Route
              path="marketing/campaigns/new"
              element={
                <RoleGate requires="crm.write">
                  <MarketingCampaignNewPage />
                </RoleGate>
              }
            />
            <Route
              path="marketing/campaigns/:id"
              element={
                <RoleGate requires="crm.write">
                  <MarketingCampaignDetailPage />
                </RoleGate>
              }
            />
            <Route
              path="marketing/segments"
              element={
                <RoleGate requires="crm.read">
                  <MarketingSegmentsPage />
                </RoleGate>
              }
            />
            <Route
              path="marketing/templates"
              element={
                <RoleGate requires="crm.write">
                  <MarketingTemplatesPage />
                </RoleGate>
              }
            />
            <Route
              path="marketing"
              element={<Navigate to="/marketing/campaigns" replace />}
            />
            <Route
              path="workspace"
              element={
                <RoleGate requires="deals.write">
                  <WorkspacePage />
                </RoleGate>
              }
            />
            <Route
              path="workspace/customer/:id"
              element={
                <RoleGate requires="deals.write">
                  <WorkspaceCustomerPage />
                </RoleGate>
              }
            />
            <Route
              path="workspace/pricing"
              element={
                <RoleGate requires="services.write">
                  <PricingPage />
                </RoleGate>
              }
            />
            <Route
              path="workspace/discounts"
              element={
                <RoleGate requires="discounts.read">
                  <DiscountsPage />
                </RoleGate>
              }
            />
            <Route
              path="workspace/referrals"
              element={
                <RoleGate requires="referrals.read">
                  <ReferralsPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/realtime"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsRealtimePage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/pages"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsPagesPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/sources"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsSourcesPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/funnels"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsFunnelsPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/attribution"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsAttributionPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/search"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsSearchPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/heatmaps"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsHeatmapsPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/replays"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsReplaysPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics/visitors"
              element={
                <RoleGate requires="analytics.read">
                  <AnalyticsVisitorsPage />
                </RoleGate>
              }
            />
            <Route
              path="analytics"
              element={<Navigate to="/analytics/realtime" replace />}
            />
            <Route path="ops/*" element={<Placeholder name="Ops" release="R2" />} />
            <Route
              path="admin/users"
              element={
                <RoleGate requires="admin.users.read">
                  <AdminUsersPage />
                </RoleGate>
              }
            />
            <Route
              path="admin/audit"
              element={
                <RoleGate requires="audit.read">
                  <AdminAuditPage />
                </RoleGate>
              }
            />
            <Route
              path="admin/service-keys"
              element={
                <RoleGate requires="admin.service_keys">
                  <ServiceKeysPage />
                </RoleGate>
              }
            />
            <Route
              path="admin/orphans"
              element={
                <RoleGate requires="crm.write">
                  <OrphansPage />
                </RoleGate>
              }
            />
            <Route
              path="admin/proxy-hub"
              element={
                <RoleGate requires="admin.users.read">
                  <ProxyHubPage />
                </RoleGate>
              }
            />
            <Route path="admin/*" element={<Placeholder name="Admin" release="R2" />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster
        position="bottom-right"
        theme="system"
        toastOptions={{
          style: {
            background: 'var(--surface-raised)',
            color: 'var(--ink-900)',
            border: '1px solid var(--hairline)',
          },
        }}
      />
    </BrowserRouter>
  )
}

function Placeholder({ name, release }: { name: string; release: string }) {
  return (
    <div className="px-8 py-10 max-w-3xl">
      <p className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-2 num">
        {release}
      </p>
      <h1 className="text-2xl font-semibold text-ink-900 tracking-tight">{name}</h1>
      <p className="text-sm text-ink-500 mt-2">
        This page is scheduled for {release}. The shell + nav exists so the
        path is reachable; real content lands when the release ships.
      </p>
    </div>
  )
}
